<template>
  <div class="glass-frosted flex flex-col relative rounded-8xl w-250 h-67.5 p-4">
    <Toolbar
      :model-value="isRecording"
      :monitor-container="monitorRef?.containerRef!"
      :disabled="disabled"
      :method="methodState"
      :can-clear="canClear"
      :can-undo="canUndo"
      :can-save="canSave"
      :delay-override="delayOverride"
      @clear="handleClear"
      @undo="handleUndo"
      @save="handleSave"
      @update:model-value="handleRecordingChange"
      @update:method="handleMethodUpdate"
      @update:delay-override="handleDelayOverride"
    />

    <div class="pt-1 h-[calc(100%-3rem)] min-h-0">
      <Monitor
        ref="monitorRef"
        :is-recording="isRecording"
        :result="result"
        :max-macro-byte-length="maxMacroByteLength"
        :enabled-wheels="enabledWheels"
        :coordinate-min="coordinateMin"
        :coordinate-max="coordinateMax"
        @request-single-recording="handleRecordingChange(true)"
        @stop-recording="handleRecordingChange(false)"
        @state-change="handleStateChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, reactive, ref, watch } from 'vue'

  import type { MacroEditorAction, MacroEditorMethod, WheelType } from '../../types'
  import Monitor from './Monitor.vue'
  import Toolbar from './Toolbar.vue'

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      result?: MacroEditorAction[]
      method?: MacroEditorMethod
      maxMacroByteLength?: number
      enabledWheels?: WheelType[]
      coordinateMin?: number
      coordinateMax?: number
      /** 开始录制前确保有可写入的宏（如空列表时先新建） */
      ensureReady?: () => Promise<boolean>
    }>(),
    {
      disabled: false,
      result: () => [],
      method: () => ({
        num: 1,
        index: 1,
        ignoreTime: false,
      }),
      maxMacroByteLength: 512,
      enabledWheels: () => [],
      coordinateMin: -2048,
      coordinateMax: 2047,
    },
  )

  const emit = defineEmits<{
    (e: 'save', payload: { data: MacroEditorAction[]; method: MacroEditorMethod }): void
  }>()

  const monitorRef = ref<{
    clearAllRecords: () => void
    clearUnsavedRecords: () => void
    saveRecords: () => MacroEditorAction[]
    hasDraftRecords: () => boolean
    applyDelayOverride: (time: number) => void
    containerRef: HTMLElement
  }>()

  const isRecording = ref(false)
  const hasRecords = ref(false)
  const hasDraft = ref(false)
  // Toolbar 延迟间隔输入框的显示状态（仅用于 Toolbar prop 同步，不传入 Monitor/MacroTag）
  const delayOverride = ref<number | null>(null)

  const methodState = reactive<MacroEditorMethod>({
    num: props.method.num,
    index: props.method.index,
    ignoreTime: props.method.ignoreTime,
  })

  const savedMethodState = reactive<MacroEditorMethod>({
    num: props.method.num,
    index: props.method.index,
    ignoreTime: props.method.ignoreTime,
  })

  const canClear = computed(() => hasRecords.value)
  const canUndo = computed(() => hasDraft.value)
  const canSave = computed(
    () =>
      hasDraft.value ||
      methodState.num !== savedMethodState.num ||
      methodState.index !== savedMethodState.index ||
      methodState.ignoreTime !== savedMethodState.ignoreTime,
  )

  const syncSavedMethodState = (value: MacroEditorMethod) => {
    savedMethodState.num = value.num
    savedMethodState.index = value.index
    savedMethodState.ignoreTime = value.ignoreTime
  }

  const handleMethodUpdate = (nextMethod: MacroEditorMethod) => {
    methodState.num = nextMethod.num
    methodState.index = nextMethod.index
    methodState.ignoreTime = nextMethod.ignoreTime
  }

  const handleStateChange = (state: { hasRecords: boolean; hasDraft: boolean }) => {
    hasRecords.value = state.hasRecords
    hasDraft.value = state.hasDraft
  }

  /**
   * 切换录制。开始前可先 ensureReady（空列表时新建宏），
   * 并保留用户在 Toolbar 上已设置的播放方式 / 延迟覆盖。
   */
  const handleRecordingChange = async (value: boolean) => {
    if (!value) {
      isRecording.value = false
      return
    }

    if (props.disabled) return

    const methodSnapshot: MacroEditorMethod = {
      num: methodState.num,
      index: methodState.index,
      ignoreTime: methodState.ignoreTime,
    }

    if (props.ensureReady) {
      const ready = await props.ensureReady()
      if (!ready) return
      // 等新建/选中引起的 props 同步完成（会短暂重置 method / 停止录制）
      await nextTick()
      await nextTick()
    }

    methodState.num = methodSnapshot.num
    methodState.index = methodSnapshot.index
    methodState.ignoreTime = methodSnapshot.ignoreTime
    isRecording.value = true
  }

  const handleClear = () => {
    if (props.disabled) return
    isRecording.value = false
    monitorRef.value?.clearAllRecords()
  }

  const handleUndo = () => {
    if (props.disabled) return
    isRecording.value = false
    monitorRef.value?.clearUnsavedRecords()
  }

  const handleSave = () => {
    if (props.disabled) return

    const data = monitorRef.value?.saveRecords() ?? []
    emit('save', {
      data,
      method: {
        num: methodState.num,
        index: methodState.index,
        // ignoreTime 暂不开放，固定为 false，避免 SDK 启发式推断的 true 被透传回去
        ignoreTime: false,
      },
    })

    isRecording.value = false
    syncSavedMethodState(methodState)
    delayOverride.value = null
  }

  const handleDelayOverride = (time: number | null) => {
    if (time != null) {
      monitorRef.value?.applyDelayOverride(time)
    }
    // 应用后立即清空，避免 Toolbar 的 watch 把已确认的值写回输入框
    delayOverride.value = null
  }

  watch(
    () => props.method,
    (value) => {
      methodState.num = value.num
      methodState.index = value.index
      methodState.ignoreTime = value.ignoreTime
      syncSavedMethodState(value)
      isRecording.value = false
    },
    { deep: true, immediate: true },
  )

  watch(
    () => props.result,
    () => {
      isRecording.value = false
    },
    { deep: true },
  )
</script>

<style scoped></style>
